import React from 'react';
import { CaseCard } from './CaseCard';
import { CaseLawyerItem } from '@/interfaces';
import { casesLawyerService } from '@/api/casesLawyer';
import { useQueryClient } from '@tanstack/react-query';
import { GET_LAWYER_CASES_LIST, GET_PINNED_CASES } from '@/api';
import { formatErrorMessage } from '@/lib';
import { HIDE_PIN_STATUSES_CASE } from '@/const/cases';
import { useHandleError } from '@/hooks/useHandleError';
import { useCasePhases } from '@/hooks';

interface Props {
	listCases: CaseLawyerItem[];
	isArchived?: boolean;
	isArchivePending?: (caseId: string) => boolean;
	onArchive?: (caseId: string) => void;
	onUnarchive?: (caseId: string) => void;
}

export const LawyerCasesGridScreen: React.FC<Props> = ({
	listCases,
	isArchived,
	isArchivePending,
	onArchive,
	onUnarchive,
}) => {
	const { caseHasError } = useHandleError();
	const queryClient = useQueryClient();
	const { getCurrentPhase } = useCasePhases();

	const onClickPin = async (caseId: string) => {
		try {
			await casesLawyerService.pinCase(caseId);
			queryClient.refetchQueries({
				queryKey: [GET_LAWYER_CASES_LIST],
			});
			queryClient.refetchQueries({
				queryKey: [GET_PINNED_CASES],
			});
		} catch (error) {
			formatErrorMessage((error as ErrorEvent)?.message);
		}
	};

	return (
		<div className="flex-1 w-full">
			<div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-6 mb-6">
				{listCases.map((item, index) => (
					<CaseCard
						hasError={caseHasError(item.id)}
						key={index}
						caseId={item.id}
						step={
							item.case_status_color === 'check' ||
							item.case_status_color === 'x'
								? item.progress_name
								: item.case_status
						}
						statusColor={item.case_status_color}
						isPinned={item.is_pinned}
						isDraft={item.is_draft}
						title={item.title}
						lawyerTasks={item.lawyer_tasks}
						onClickPin={() => onClickPin(item.id)}
						showPin={!HIDE_PIN_STATUSES_CASE.includes(item.progress_name)}
						getCurrentPhase={getCurrentPhase}
						createdAt={item.created_at}
						totalClients={item.total_clients}
						totalMessages={item.total_messages}
						isClosedToNewClients={item.status === 'Closed to new clients'}
						isHideCase={!item.visible}
						isArchived={isArchived}
						isArchivePending={isArchivePending?.(item.id)}
						onArchive={onArchive}
						onUnarchive={onUnarchive}
						progressName={item.progress_name}
					/>
				))}
			</div>
		</div>
	);
};
